/**
 * Convert a `File` to a base64-encoded string suitable for the BE's
 * `uploadVisitorMedia` mutation. Strips the `data:<mime>;base64,`
 * prefix the FileReader emits — the BE wants raw base64 bytes only.
 *
 * Returns the decoded byte count alongside the payload so the
 * caller can check it against `WEB_UPLOAD_MAX_BYTES` (10 MB on the
 * BE) BEFORE invoking the mutation. A short-circuit at the FE saves
 * a 1 MB+ network round-trip on oversized files.
 */
export async function fileToBase64(file: File): Promise<{
  base64: string;
  size: number;
}> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("FileReader returned non-string result"));
        return;
      }
      // `result` is `data:<mime>;base64,<payload>` — strip the prefix.
      const comma = result.indexOf(",");
      const payload = comma >= 0 ? result.slice(comma + 1) : result;
      resolve({ base64: payload, size: file.size });
    };
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

/** Mime allowlist — must match the BE's `WebUploadService.ALLOWED_MIMES`.
 *  Keeping these in lockstep prevents the FE from picking a file the
 *  BE will reject. Covers both mobile platforms:
 *   - iOS photos default to HEIC/HEIF, videos to QuickTime (.mov),
 *     voice memos to audio/mp4 (.m4a).
 *   - Android photos are usually JPEG/WebP, videos MP4/3GPP. */
export const ALLOWED_UPLOAD_MIMES = new Set<string>([
  // Images
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  // Audio (voice memos)
  "audio/mpeg",
  "audio/mp4",
  "audio/ogg",
  "audio/webm",
  "audio/wav",
  // Video
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/3gpp",
  // Documents
  "application/pdf",
]);

/** Extension → MIME fallback for when the browser reports an empty or
 *  unknown `File.type`. Mobile camera captures frequently arrive with
 *  an empty `File.type`, which would otherwise fail the allow-list
 *  check and never reach the BE. */
const EXT_TO_MIME: Record<string, string> = {
  // Images
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  // Audio
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  ogg: "audio/ogg",
  oga: "audio/ogg",
  wav: "audio/wav",
  weba: "audio/webm",
  // Video
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  "3gp": "video/3gpp",
  // Documents
  pdf: "application/pdf",
};

/**
 * The effective upload MIME: the browser's `File.type` when it reports
 * one, else inferred from the filename extension. Mobile (especially
 * iOS camera) often hands us a file with an empty `File.type`; without
 * this fallback the allow-list check rejects a perfectly valid photo.
 */
export function resolveUploadMime(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_TO_MIME[ext] ?? "";
}

/** 10 MB — matches the BE's `MAX_UPLOAD_BYTES`. */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
